import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Fab,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateApproval from "./CreateApproval";
import ApprovalCard from "./ApprovalCard";
import { Approval, ApprovalStatus, ApprovalFormData } from "./approvalsTypes";
import { useApprovals } from "../pages/contractor/useApprovals";

interface ApprovalsListProps {
  projectId: string;
  contractorId: string;
  onApprovalCreated?: (approval: Approval) => void;
  onApprovalDeleted?: (approvalId: string) => void;
}

const ApprovalsList: React.FC<ApprovalsListProps> = ({
  projectId,
  contractorId,
  onApprovalCreated,
  onApprovalDeleted,
}) => {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(
    null,
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(
    null,
  );

  const { fetchApprovals, saveApproval, updateApproval, deleteApproval } =
    useApprovals(projectId, contractorId);

  const statusFilterValues: ApprovalStatus[] = [
    "pending",
    "approved",
    "rejected",
    "discussion",
  ];

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    const data = await fetchApprovals();
    setApprovals(data);
    setLoading(false);
  }, [fetchApprovals]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleCreateApproval = async (formData: ApprovalFormData) => {
    setSaveLoading(true);
    const newApproval = await saveApproval(formData);
    setSaveLoading(false);

    if (newApproval) {
      await loadApprovals();
      onApprovalCreated?.(newApproval);
      setCreateDialogOpen(false);
    } else {
      setError("Failed to create approval request. Please try again.");
    }
  };

  const openEditDialog = (approval: Approval) => {
    setSelectedApproval(approval);
    setEditDialogOpen(true);
  };

  const handleEditApproval = async (formData: ApprovalFormData) => {
    if (!selectedApproval) return;

    setSaveLoading(true);
    const updated = await updateApproval(selectedApproval.id, formData);
    setSaveLoading(false);

    if (updated) {
      await loadApprovals();
      setEditDialogOpen(false);
      setSelectedApproval(null);
    } else {
      setError("Failed to update approval request. Please try again.");
    }
  };

  const handleDeleteApproval = async () => {
    if (!selectedApprovalId) return;

    const success = await deleteApproval(selectedApprovalId);
    if (success) {
      await loadApprovals();
      onApprovalDeleted?.(selectedApprovalId);
      setDeleteDialogOpen(false);
      setSelectedApprovalId(null);
    } else {
      setError("Failed to delete approval request. Please try again.");
    }
  };

  const openDeleteDialog = (approvalId: string) => {
    setSelectedApprovalId(approvalId);
    setDeleteDialogOpen(true);
  };

  const filteredApprovals = approvals.filter(
    (approval) => approval.status === statusFilterValues[currentTab],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {statusFilterValues.map((status) => {
          const count = approvals.filter((a) => a.status === status).length;
          return (
            <Tab
              key={status}
              label={`${status.charAt(0).toUpperCase() + status.slice(1)} (${count})`}
            />
          );
        })}
      </Tabs>

      {filteredApprovals.length === 0 ? (
        <Alert severity="info">
          No {statusFilterValues[currentTab]} approvals yet.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {filteredApprovals.map((approval) => (
            <ApprovalCard
              key={approval.id}
              approval={approval}
              onEdit={() => openEditDialog(approval)}
              onDelete={() => openDeleteDialog(approval.id)}
              showActions={true}
              projectId={projectId}
              contractorId={contractorId}
              currentUserRole="contractor"
              currentUserName="You"
              onStatusChanged={() => loadApprovals()}
            />
          ))}
        </Stack>
      )}

      {/* Create Approval Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Approval Dialog */}
      <CreateApproval
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateApproval}
        isLoading={saveLoading}
      />

      {/* Edit Approval Dialog */}
      <CreateApproval
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedApproval(null);
        }}
        onSave={handleEditApproval}
        isLoading={saveLoading}
        initialApproval={selectedApproval}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Approval Request?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this approval request? This action
          cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteApproval}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalsList;
